import type { Flow } from '@/api/flows/flows-types'
import type { AdditionalFile, VariobendFile } from '@/api/items/items-types'
import type { Detail, StandardTimesKeys } from '@/api/orders/orders-types'
import type { ProductionLog } from '@/api/production-logs/production-logs-types'
import type { DetailStatus } from '@/components/common/selectors/detail-status-selector/config/statuses'
import { getMimeTypeFromUrl } from '@/components/common/view-files/utils/files'
import { appendQueryParam, isPresignedUrl } from '@/utils/url'

export const getAllProdLogsDone = ({ logs, flows }: { logs: ProductionLog[]; flows: Flow[] }) => {
  if (logs.length === 0) return false

  const getFlowInvenSet = (arr: Flow[]) => new Set(arr?.map((item) => item?.inven).filter(Boolean))
  const getLogInvenSet = (arr: ProductionLog[]) =>
    new Set(arr?.map((item) => item?.flow_inven)?.filter(Boolean))

  const allEndTimesFilled = (arr: ProductionLog[]) => arr?.every((item) => !!item?.end_time)

  const set1 = getLogInvenSet(logs)
  const set2 = getFlowInvenSet(flows)

  const sameInven = set1.size === set2.size && [...set1]?.every((value) => set2.has(value))

  return sameInven && allEndTimesFilled(logs)
}

export const getVariobendFile = ({ files }: { files: VariobendFile[] }) => {
  const imageVariobendFiles = files.filter((file) => {
    const fileType = getMimeTypeFromUrl(file.full_path)
    return fileType.startsWith('image/')
  })
  const newestVariobendFile = [...imageVariobendFiles].sort(
    (a, b) => new Date(b.last_modified).getTime() - new Date(a.last_modified).getTime()
  )[0]

  return newestVariobendFile
}

// A drawing's URL, versioned by last_modified so a re-upload busts the cache.
// Signed (private-bucket) links come back unchanged - appendQueryParam never
// touches them, an extra parameter would break their signature.
export const getDrawingUrl = (file: VariobendFile | undefined) =>
  appendQueryParam(file?.full_path, 'v', file?.last_modified)

export const getOriginalDetailImage = (detail: Detail, bustParam?: number) => {
  const newestVariobendImage = getVariobendFile({ files: detail.variobend_files })
  const base = getDrawingUrl(newestVariobendImage)
  return bustParam ? appendQueryParam(base, 'bust', String(bustParam)) : base
}

// The edited image is overwritten in place at a stable S3 file_path, and the
// row's created_at/updated_at don't reliably change on overwrite — so a
// timestamp-based `?v=` can't bust the browser cache when annotations change.
// Hash the canvas JSON instead: the URL then changes iff the content does, so a
// background refetch (or another user's edit) refreshes the thumbnail without a
// manual re-save. Falls back to timestamps when additional_info is absent.
const hashContent = (value: unknown): string => {
  const str = JSON.stringify(value) ?? ''
  let hash = 5381
  for (let i = 0; i < str.length; i++) hash = (hash * 33) ^ str.charCodeAt(i)
  return (hash >>> 0).toString(36)
}

const getAdditionalFileVersion = (file: AdditionalFile): string | number => {
  if (file.additional_info != null) return hashContent(file.additional_info)
  return file.updated_at ?? file.created_at
}

// The edited PNG bakes in the drawing it was annotated on. Once that drawing is
// re-uploaded (new file or newer last_modified), the PNG shows the old drawing —
// detect it from the background src (`?v=last_modified`) saved in the canvas JSON,
// or for a signed src, from when the edit was saved.
export const isAdditionalFileStale = (
  file: AdditionalFile | undefined,
  drawing: VariobendFile | undefined
) => {
  if (!file || !drawing) return false
  const src: string | undefined = file.additional_info?.objects?.find(
    (obj: { type?: string }) => obj?.type?.toLowerCase() === 'image'
  )?.src
  if (!src) return false
  const drawnOn = new URL(src, window.location.origin)
  const current = new URL(drawing.full_path, window.location.origin)
  // Other origin (legacy corsproxy, old host): can't tell what it was drawn on.
  if (drawnOn.origin !== current.origin) return false
  if (drawnOn.pathname !== current.pathname) return true
  const version = drawnOn.searchParams.get('v')
  if (version !== null) return version !== drawing.last_modified
  // Signed links carry no ?v= (it would break the signature): the drawing is
  // stale if it was re-uploaded after the edit was saved.
  if (!isPresignedUrl(src)) return false
  return parseServerDate(drawing.last_modified) > parseServerDate(file.updated_at ?? file.created_at)
}

// Backend timestamps look like `2026-10-21 11:51:10 EDT-0400`, which only some
// browsers' Date parse. NaN (unparseable) compares false, so the edit is kept.
const parseServerDate = (value: string | null | undefined) => {
  const m = value?.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}).*?([+-]\d{2}):?(\d{2})$/)
  return m ? Date.parse(`${m[1]}T${m[2]}${m[3]}:${m[4]}`) : NaN
}

// The canvas JSON saves the drawing's link as it was at save time. A signed link
// expires (R2_PRESIGNED_SECONDS), so a day later the editor can't load it and
// opens blank — point it at the drawing's current link instead.
export const withCurrentDrawing = (json: any, drawing: VariobendFile | undefined) => {
  if (!drawing || !Array.isArray(json?.objects)) return json
  return {
    ...json,
    objects: json.objects.map((obj: { type?: string; src?: string }) =>
      obj?.type?.toLowerCase() === 'image' && isPresignedUrl(obj.src)
        ? { ...obj, src: getDrawingUrl(drawing) }
        : obj
    )
  }
}

export const getCurrentDetailImage = (detail: Detail, bustParam?: number) => {
  const newestVariobendImage = getVariobendFile({ files: detail.variobend_files })

  const lastFile = detail?.additional_files?.[detail?.additional_files?.length - 1]
  const lastAdditionalFile = isAdditionalFileStale(lastFile, newestVariobendImage) ? undefined : lastFile

  const filePath = lastAdditionalFile?.file_path?.replace(
    /(https?:\/\/[^/]+\.amazonaws\.com\/)\1/,
    '$1'
  )

  const base = lastAdditionalFile
    ? appendQueryParam(filePath, 'v', getAdditionalFileVersion(lastAdditionalFile))
    : appendQueryParam(newestVariobendImage?.full_path, 'v', newestVariobendImage?.last_modified)

  return bustParam ? appendQueryParam(base, 'bust', String(bustParam)) : base
}

export const getCurrentFlow = (status: DetailStatus | null | undefined, flows: Flow[]) => {
  switch (status) {
    case 'cutting':
      return flows.find((flow) => flow?.inven === 'CUTTIME')
    case 'cut':
      return flows.find(
        (flow) =>
          flow?.inven === 'PUNCHTIME' ||
          flow?.inven === 'FT' ||
          flow?.inven === 'VB' ||
          flow?.inven === 'SOFFITTIME' ||
          flow?.inven === 'TRIMACCESSTIME' ||
          flow?.inven === 'BBTIME'
      )
    case 'punching':
      return flows.find((flow) => flow?.inven === 'PUNCHTIME')
    case 'punched':
      return flows.find((flow) => flow?.inven === 'FT' || flow.inven === 'VB')
    case 'bending':
      return flows.find((flow) => flow?.inven === 'FT' || flow.inven === 'VB')
    case 'siding':
    case 'siding_done':
      return flows.find((flow) => flow?.inven === 'BBTIME')
    case 'trim':
    case 'trim_done':
      return flows.find((flow) => flow?.inven === 'TRIMACCESSTIME')
    case 'soffit':
    case 'soffit_done':
      return flows.find((flow) => flow?.inven === 'SOFFITTIME')
    case 'ready_for_pack':
      return flows.find((flow) => flow?.inven === 'FT' || flow.inven === 'VB')
    default:
      return flows.find((flow) => flow?.inven === 'CUTTIME') ?? flows[0]
  }
}

export const getTargetFlow = ({
  detail,
  flows
}: {
  detail: Detail
  flows: Flow[] | undefined
}): Flow | undefined => {
  if (!flows || !detail?.standard_times) return undefined

  const applicableFlows = [...flows]
    .filter((flow) => Object.keys(detail.standard_times).includes(flow.inven))
    .sort((a, b) => a.position - b.position)

  const doneInvens = new Set(
    (detail.production_logs ?? []).filter((log) => !!log.end_time).map((log) => log.flow_inven)
  )

  return applicableFlows.find((flow) => !doneInvens.has(flow.inven))
}

export const getFlowInfo = ({ detail, flows }: { flows: Flow[] | undefined; detail: Detail }) => {
  if (!flows || !detail?.production_logs?.length) {
    return {
      currentFlows: [] as Flow[],
      currentFlow: undefined,
      nextFlow: undefined,
      isLastFlow: false
    }
  }

  const currentFlows =
    flows?.filter((flow) =>
      detail?.standard_times ? Object?.keys(detail?.standard_times).includes(flow?.inven) : false
    ) ?? []

  const currentFlow = getCurrentFlow(detail?.item?.status, currentFlows)
  const currentFlowIndex = currentFlows.findIndex((flow) => flow.id === currentFlow?.id)!

  const nextFlow = currentFlowIndex !== -1 ? currentFlows[currentFlowIndex + 1] : null

  const currentLog = detail.production_logs.at(-1)

  const nextLog = detail?.production_logs?.find(
    (log) => log.flow_inven === currentLog?.flow_inven && log.id !== currentLog?.id
  )

  const prevFlow: Flow | undefined = currentFlows[currentFlowIndex - 1]

  return { currentFlows, currentFlow, nextFlow, prevFlow, currentLog, isLastFlow: !nextFlow, nextLog }
}

const NEXT_PROCESS_ROW_BG: Record<StandardTimesKeys, string> = {
  CUTTIME: 'bg-blue-100/35',
  PUNCHTIME: 'bg-red-100/35',
  VB: 'bg-cyan-100/35',
  FT: 'bg-yellow-100/35',
  PKGT: 'bg-purple-100/35',
  ASSEMBLYTIME: 'bg-purple-100/35',
  BBTIME: 'bg-green-100/35',
  TRIMACCESSTIME: 'bg-blue-100/35',
  SOFFITTIME: 'bg-purple-100/35'
}

export type MarkDoneStatus =
  | { canComplete: true; logId: number; cartId: number }
  | { canComplete: false; reason: string }

export const getMarkDoneStatus = ({
  detail,
  flows,
  flowInven,
  fallbackCartId
}: {
  detail: Detail
  flows: Flow[] | undefined
  flowInven: StandardTimesKeys | undefined
  fallbackCartId?: number
}): MarkDoneStatus => {
  if (!flowInven) return { canComplete: false, reason: 'No flow' }

  const applicableFlows = (flows ?? [])
    .filter((flow) => Object.keys(detail?.standard_times ?? {}).includes(flow.inven))
    .sort((a, b) => a.position - b.position)

  const flowIdx = applicableFlows.findIndex((f) => f.inven === flowInven)
  if (flowIdx === -1) return { canComplete: false, reason: 'Flow not applicable' }

  const log = detail.production_logs?.find((l) => l.flow_inven === flowInven)
  if (!log) return { canComplete: false, reason: 'No production log' }
  if (!log.start_time) return { canComplete: false, reason: 'Not started' }
  if (log.end_time) return { canComplete: false, reason: 'Already done' }
  if (detail.item?.is_blocked) return { canComplete: false, reason: 'Blocked' }

  const previousFlows = applicableFlows.slice(0, flowIdx)
  const hasIncompletePrev = previousFlows.some((f) => {
    const prevLog = detail.production_logs?.find((l) => l.flow_inven === f.inven)
    return !prevLog?.end_time
  })
  if (hasIncompletePrev) return { canComplete: false, reason: 'Previous stage not done' }

  const isLastFlow = flowIdx === applicableFlows.length - 1
  const effectiveCartId = log.cart_id ?? fallbackCartId
  if (!isLastFlow && !effectiveCartId) return { canComplete: false, reason: 'No cart assigned' }

  return { canComplete: true, logId: log.id, cartId: effectiveCartId ?? 0 }
}

export const getNextFlowAfter = ({
  detail,
  flows,
  afterInven
}: {
  detail: Detail
  flows: Flow[] | undefined
  afterInven: StandardTimesKeys | undefined
}): Flow | undefined => {
  if (!flows || !detail?.standard_times || !afterInven) return undefined

  const applicableFlows = [...flows]
    .filter((flow) => Object.keys(detail.standard_times).includes(flow.inven))
    .sort((a, b) => a.position - b.position)

  const currentIdx = applicableFlows.findIndex((flow) => flow.inven === afterInven)
  if (currentIdx === -1) return undefined

  return applicableFlows[currentIdx + 1]
}

export const getNextProcessRowClass = ({
  detail,
  flows,
  afterInven
}: {
  detail: Detail
  flows: Flow[] | undefined
  afterInven?: StandardTimesKeys
}): string | undefined => {
  const nextFlow = afterInven
    ? getNextFlowAfter({ detail, flows, afterInven })
    : getTargetFlow({ detail, flows })
  return nextFlow ? NEXT_PROCESS_ROW_BG[nextFlow.inven] : undefined
}
